/* eslint-disable camelcase */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { CircularProgress, FormControl, IconButton, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Tooltip from '@material-ui/core/Tooltip';

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { AppConfig } from '../../../AppConfig';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { CommonTheme } from '../../../styles/Styles';
import { PageDialog } from '../../../common/components/PageDialog';
import { SearchBar } from '../../../common/components/SearchBar';
import WhatsappFlowPreview from '../../../common/components/WhatsappFlowPreview';
import { hooksBridgeErrorMessage } from './WhatsappTemplateDetail';
import { buildWaNumberLabel, buildWaNumberSelectOptions, extractCrmNumbersItems, getWaOrgAccountNumbers, withVendorProjectIdQueryParam } from './WhatsappUtils';

const MAX_FLOW_FIELDS = 10
const FLOW_FIELD_TYPES_NOT_NONE = ['TextInput', 'RadioButtonsGroup', 'CheckboxGroup']
const FLOW_GROUP_TYPES = ['RadioButtonsGroup', 'CheckboxGroup']

const FLOW_PREVIEW_CONFIG = {
  component: 'flow_preview_adapter',
  title: 'Preview',
  width: 360,
  paddingBottom: 24,
}

function normalizeFlowsResponse(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  if (Array.isArray(data.flows)) return data.flows
  if (Array.isArray(data.data)) return data.data
  return []
}

function buildBulkCopyFlowFormFieldDef(availableWabas,currentWaba) {
  const filteredWabas = availableWabas.filter(waba => waba.key !== currentWaba)
  return {
    title: 'Bulk Copy Flow',
    ctaButtonText: 'Submit',
    formType: 'edit',
    steps: [
      {
        fields: [
          {
            key: 'selectedWabas',
            type: 'check_box',
            label: 'Select WABA numbers (max 10)',
            tip: 'Choose one or more WhatsApp Business Account numbers to copy the flow to',
            options: filteredWabas.map(waba => ({
              key: waba.key,
              label: waba.label || waba.key
            })),
            v_required: {
              value: true,
              err: 'Select at least one WABA number'
            },
            placement: 'left',
            defaultValue: [],
          }
        ]
      }
    ]
  }
}

function statusBadgeStyle(status) {
  const s = String(status || '').toUpperCase()
  if (['PUBLISHED', 'APPROVED', 'ACTIVE', 'LIVE'].includes(s)) return Styles.statusBadgeSuccess
  if (['DRAFT', 'PENDING', 'IN_REVIEW'].includes(s)) return Styles.statusBadgePending
  if (['REJECTED', 'FAILED', 'DISABLED'].includes(s)) return Styles.statusBadgeError
  return Styles.statusBadgeDefault
}

function filterFlowRows(masterList, wabaSelected, searchText) {
  let rows = masterList || []
  if (wabaSelected && wabaSelected.key) {
    rows = rows.filter(r => r.wabaNumber === wabaSelected.key)
  }
  const q = (searchText || '').trim().toLowerCase()
  if (q) {
    rows = rows.filter(r => String(r.name || '').toLowerCase().includes(q))
  }
  return rows
}

function splitPipeOptions(options) {
  return String(options || '').split('|').map(s => s.trim()).filter(Boolean)
}

function joinPipeOptions(options) {
  if (Array.isArray(options)) {
    return options.map(o => String(o || '').trim()).filter(Boolean).join('|')
  }
  return String(options || '').trim()
}

function parsedFieldsToListRows(parsedFields) {
  return (parsedFields || []).slice(0, MAX_FLOW_FIELDS).map((f, i) => ({
    id: `lr_field_${i}_${Date.now().toString(36)}`,
    Type: f.type,
    Label: f.label || '',
    Required: f.required ? 'YES' : 'NO',
    Options: splitPipeOptions(f.options),
  }))
}

/** Form definition for the list-page “Add flow” modal. */
function buildWhatsappFlowAddModalFieldDef(fromNumbers, waNumberDetails) {
  return {
    title: 'Add WhatsApp flow',
    ctaButtonText: 'Submit',
    preview: FLOW_PREVIEW_CONFIG,
    steps: [{
      fields: [
        {
          key: 'wabaNumber',
          type: 'radio',
          label: 'WABA Number',
          tip: 'WhatsApp Business Account phone number',
          options: buildWaNumberSelectOptions(fromNumbers, waNumberDetails),
          v_required: { value: true, err: 'Select WABA number' },
          placement: 'left',
        },
        {
          key: 'name',
          type: 'edit_text',
          label: 'Name',
          tip: 'snake_case: lowercase letters, digits, underscores (start with a letter)',
          v_required: { value: true, err: 'Provide flow name' },
          placement: 'right',
        },
        {
          key: 'screenTitle',
          type: 'edit_text',
          label: 'Screen title',
          tip: 'Heading shown at the top of the flow screen',
          v_required: { value: true, err: 'Provide screen title' },
          placement: 'left',
        },
        {
          key: 'footerButtonLabel',
          type: 'edit_text',
          label: 'Footer button label',
          tip: 'Label on the submit button at the bottom of the screen',
          v_required: { value: true, err: 'Provide footer button label' },
          placement: 'right',
        },
        {
          key: 'field',
          type: 'list',
          v_required: { value: false, err: '' },
          listConfig: {
            title: 'Fields',
            addButtonText: 'Add Field',
            maxItems: MAX_FLOW_FIELDS,
            minItems: 1,
            rowFields: [
              {
                key: 'Type',
                type: 'radio',
                label: 'Type',
                tip: 'Add up to 10 fields. Text input, radio (one choice), or checkboxes (multiple)',
                options: [
                  { key: 'TextInput', label: 'TEXT INPUT' },
                  { key: 'RadioButtonsGroup', label: 'RADIO BUTTONS' },
                  { key: 'CheckboxGroup', label: 'CHECKBOX BUTTONS' },
                ],
                v_required: { value: true, err: 'Select type' },
              },
              {
                key: 'Label',
                type: 'edit_text',
                label: 'Label',
                tip: 'Question shown to the user on the flow screen',
                v_required: { value: true, err: 'Provide label' },
              },
              {
                key: 'Required',
                type: 'radio',
                label: 'Required',
                tip: 'Whether the user must fill this field',
                options: [
                  { key: 'YES', label: 'YES' },
                  { key: 'NO', label: 'NO' },
                ],
                v_required: { value: true, err: 'Select whether required' },
              },
              {
                key: 'Options',
                type: 'check_box',
                addOptions: true,
                label: 'Options',
                tip: 'Add choice labels, e.g. Starter, Growth, Premium',
                options: [],
                condition: { key: 'Type', values: FLOW_GROUP_TYPES },
                v_required: { value: true, err: 'Provide options' },
              },
            ],
          },
        },
      ],
    }],
  }
}

function isSnakeCaseFlowName(name) {
  const s = String(name || '').trim()
  if (!s) return false
  return /^[a-z][a-z0-9_]*$/.test(s)
}

function buildSingleScreenFlowFromForm(formData) {
  const fields = []
  for (let n = 1; n <= MAX_FLOW_FIELDS; n += 1) {
    const t = formData[`field${n}Type`]
    if (!t || t === 'NONE') break
    const label = String(formData[`field${n}Label`] || '').trim()
    const reqRaw = formData[`field${n}Required`]
    const required = reqRaw === 'YES'
    const entry = {
      type: t,
      label,
      required,
    }
    if (FLOW_GROUP_TYPES.includes(t)) {
      entry.options = joinPipeOptions(formData[`field${n}Options`])
    }
    fields.push(entry)
  }
  return {
    name: String(formData.name || '').trim(),
    screenTitle: String(formData.screenTitle || '').trim(),
    footerButtonLabel: String(formData.footerButtonLabel || '').trim(),
    fields,
  }
}

function validateFlowFormBeforeSubmit(formData) {
  if (!isSnakeCaseFlowName(formData.name)) {
    return 'Flow name must be snake_case: start with a lowercase letter; use only lowercase letters, digits, and underscores.'
  }
  const flow = buildSingleScreenFlowFromForm(formData)
  if (!flow.screenTitle) return 'Provide screen title.'
  if (!flow.footerButtonLabel) return 'Provide footer button label.'
  for (let i = 0; i < flow.fields.length; i += 1) {
    const f = flow.fields[i]
    if (!f.label) return `Field ${i + 1}: label is required.`
    if (FLOW_GROUP_TYPES.includes(f.type)) {
      if (!f.options) return `Field ${i + 1}: options are required for ${f.type}.`
    }
  }
  return null
}

export class WhatsappFlows extends BaseEntityPageSlixta {
  constructor(props) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'WhatsApp flows'
    this.pageKey = 'whatsapp-flows'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.noAdd = true
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      {
        key: 'waAccounts',
        ms: this.ms,
        method: 'GET',
        paths: ['miscs', 'get-wa-accounts'],
        queryParams: { orgId: this.orgId },
      },
      {
        key: 'crmnumbers',
        ms: this.ms,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId: this.orgId },
      },
    ]
    this.apiData = {
      orgId: this.orgId,
    }
    this.tableHeadersMap = {
      Index: current => current.__index + 1,
      Name: current => (
        <div>
          <span className={css(Styles.line1)}>{current.name || '—'}</span>
        </div>
      ),
      Status: current => (
        <div>
          <span className={css(Styles.statusBadge, statusBadgeStyle(current.status))}>
            {current.status || '—'}
          </span>
        </div>
      ),
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = [
      {
        label: 'Add flow',
        onClick: () => this.openModalForAddFlow(),
      },
    ]
    this.state = {
      ...this.state,
      masterFlowList: [],
      wabaFilterSelected: null,
      wabaOptions: [],
      searchText: '',
      waFromNumbers: [],
      waNumberDetails: undefined,
      flowsByWaba: {},
      wabaFlowsLoading: false,
      isFlowPreviewDialogOpen: false,
      flowPreviewTitle: '',
      flowPreviewPayload: null,
      flowPreviewLoadingId: '',
    }
  }

  fetchItems(onReadyCallback) {
    const prev = this._waFlowsFetchCount || 0
    this._waFlowsFetchCount = prev + 1
    if (prev > 0) {
      this.setState({ loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD })
    }
    super.fetchItems(onReadyCallback)
  }

  buildRowsForWaba = async (fromNumber) => {
    if (!fromNumber) return []
    const data = await ApiHelper.callAwait({
      ms: this.ms,
      method: 'GET',
      paths: ['hooks'],
      queryParams: withVendorProjectIdQueryParam({
        orgId: this.orgId,
        fromNumber,
        bridgePath: 'whatsappflows',
      }, fromNumber, this.crmNumbers),
    })
    const list = normalizeFlowsResponse(data)
    return list.map((row) => {
      const name = String(row.name || '').trim()
      const status = row.status || row.flowStatus || row.state || ''
      const id = row.id != null ? String(row.id) : `${fromNumber}_${name}`
      return {
        id,
        name: name || '—',
        status,
        wabaNumber: fromNumber,
      }
    })
  }

  applyRowsForSelectedWaba = (rows, selectedWaba, extras = {}) => {
    const filteredRows = filterFlowRows(rows, selectedWaba, '')
    this.setState({
      ...extras,
      masterFlowList: rows,
      wabaFilterSelected: selectedWaba,
      searchText: '',
      items: filteredRows,
    })
  }

  async onFetchSuccess(results) {
    const waAccounts = this.getResult(results, 'waAccounts')
    const waOrgAccounts = waAccounts && waAccounts[this.orgId]
    const { fromNumbers, waNumberDetails } = getWaOrgAccountNumbers(waOrgAccounts)
    this.waNumberDetails = waNumberDetails
    this.crmNumbers = extractCrmNumbersItems(this.getResult(results, 'crmnumbers'))
    const wabaOptions = buildWaNumberSelectOptions(fromNumbers, waNumberDetails)
    const firstWaba = wabaOptions[0] || null
    const currentSelectedKey = this.state.wabaFilterSelected && this.state.wabaFilterSelected.key
    const selectedWaba = wabaOptions.find(w => w.key === currentSelectedKey) || firstWaba
    if (!selectedWaba) {
      this.setState({
        masterFlowList: [],
        wabaFilterSelected: null,
        wabaOptions: [],
        searchText: '',
        items: [],
        waFromNumbers: fromNumbers,
        waNumberDetails,
        flowsByWaba: {},
      })
      return
    }
    try {
      const rows = await this.buildRowsForWaba(selectedWaba.key)
      this.applyRowsForSelectedWaba(rows, selectedWaba, {
        wabaOptions,
        waFromNumbers: fromNumbers,
        waNumberDetails,
        flowsByWaba: { [selectedWaba.key]: rows },
      })
    } catch (e) {
      this.applyRowsForSelectedWaba([], selectedWaba, {
        wabaOptions,
        waFromNumbers: fromNumbers,
        waNumberDetails,
        flowsByWaba: {},
      })
    }
  }

  openModalForAddFlow = () => {
    const fromNumbers = this.state.waFromNumbers || []
    const filterKey = this.state.wabaFilterSelected && this.state.wabaFilterSelected.key
    const wabaFromFilter =
      filterKey && fromNumbers.includes(filterKey) ? filterKey : (fromNumbers[0] || '')
    const waNumberDetails = this.state.waNumberDetails
    const fd = JSON.parse(JSON.stringify(buildWhatsappFlowAddModalFieldDef(fromNumbers, waNumberDetails)))
    fd.formType = 'add'
    const wabaField = (fd.steps[0].fields || []).find(f => f.key === 'wabaNumber')
    if (wabaField && wabaFromFilter) {
      wabaField.options = [{ label: buildWaNumberLabel(wabaFromFilter, waNumberDetails), key: wabaFromFilter }]
      fd.disableAlwaysFormFields = [...(fd.disableAlwaysFormFields || []), 'wabaNumber']
    }
    Helper.deleteFormDefinitionValues(fd)
    const populate = {
      wabaNumber: wabaFromFilter,
      name: Helper.nextNonClashingIndexedName(
        'new_flow_',
        (this.state.masterFlowList || []).map(r => r && r.name)
      ),
      screenTitle: '',
      footerButtonLabel: '',
      field: [],
    }
    Helper.populateFormDefinitionValues(fd, populate)
    this.formDefinition = fd
    this.setState({
      modalOpen: true,
      apiData: { waFlowInitialAdd: true },
      apiMethod: 'POST',
      apiEndPoint: '',
    })
  }
  openCopyTemplateDialog = async (current) => {
    if (!current || !current.id || !current.wabaNumber) {
      this.showMessage('Unable to copy this flow.', undefined, 'Error')
      return
    }

    try {
      const hookData = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'GET',
        paths: ['hooks', current.id],
        queryParams: withVendorProjectIdQueryParam({
          orgId: this.orgId,
          fromNumber: current.wabaNumber,
          bridgePath: 'whatsappflows',
        }, current.wabaNumber, this.crmNumbers),
      })

      if (!hookData || !hookData._flowJson) {
        this.showMessage('Unable to fetch flow details for copying.', undefined, 'Error')
        return
      }

      const flowJson = hookData._flowJson
      const screens = flowJson.screens || []
      if (screens.length === 0) {
        this.showMessage('Flow has no screens. Cannot create a copy.', undefined, 'Error')
        return
      }

      const screen = screens[0]
      const screenTitle = screen.title || ''

      if (!screenTitle) {
        this.showMessage('Screen title is missing. Cannot create a copy.', undefined, 'Error')
        return
      }

      let formFields = []
      if (screen.layout && screen.layout.children && screen.layout.children.length > 0) {
        const layoutChild = screen.layout.children[0]
        if (layoutChild.children) {
          formFields = layoutChild.children
        }
      }

      const parsedFields = []
      let footerButtonLabel = ''

      for (let i = 0; i < formFields.length && parsedFields.length < MAX_FLOW_FIELDS; i += 1) {
        const field = formFields[i]
        const fieldType = field.type

        if (fieldType === 'Footer') {
          footerButtonLabel = field.label || ''
          continue
        }

        if (!FLOW_FIELD_TYPES_NOT_NONE.includes(fieldType)) {
          continue
        }

        const label = field.label || ''
        const required = field.required === true

        const entry = {
          type: fieldType,
          label,
          required,
        }

        if (FLOW_GROUP_TYPES.includes(fieldType) && field['data-source']) {
          const options = field['data-source']
            .map(opt => opt.title || opt.id)
            .join('|')
          entry.options = options
        }

        parsedFields.push(entry)
      }

      if (!footerButtonLabel) {
        this.showMessage(
          'Footer button label is missing. Cannot create a copy.',
          undefined,
          'Error'
        )
        return
      }

      const fromNumbers = this.state.waFromNumbers || []
      const waNumberDetails = this.state.waNumberDetails
      const fd = JSON.parse(JSON.stringify(buildWhatsappFlowAddModalFieldDef(fromNumbers, waNumberDetails)))
      fd.formType = 'edit'

      const wabaField = (fd.steps[0].fields || []).find(f => f.key === 'wabaNumber')
      if (wabaField) {
        wabaField.options = buildWaNumberSelectOptions(this.state.waFromNumbers || [], waNumberDetails)
        fd.disableAlwaysFormFields = (fd.disableAlwaysFormFields || []).filter(f => f !== 'wabaNumber')
      }
      this.flowCopySourceWaba = current.wabaNumber
      Helper.deleteFormDefinitionValues(fd)

      const populate = {
        wabaNumber: current.wabaNumber,
        name: Helper.nextNonClashingIndexedName(
          `${current.name}_copy_`,
          (this.state.masterFlowList || []).map(r => r && r.name)
        ),
        screenTitle,
        footerButtonLabel,
        field: parsedFieldsToListRows(parsedFields),
      }

      Helper.populateFormDefinitionValues(fd, populate)
      this.formDefinition = fd

      this.setState({
        modalOpen: true,
        apiData: { waFlowInitialCopy: true },
        apiMethod: 'POST',
        apiEndPoint: '',
      })
    } catch (e) {
      this.showMessage('Unable to load flow details for copying.', undefined, 'Error')
    }
  }
  async onFormFieldOptionAdd({ formField, value }) {
    if (!formField || value == null) {
      return (formField && formField.options) || []
    }
    if (!formField.options) {
      formField.options = []
    }
    const trimmed = String(value).trim()
    if (!trimmed) {
      return formField.options
    }
    if (!formField.options.some((o) => o.key === trimmed)) {
      formField.options.push({ key: trimmed, label: trimmed })
    }
    return formField.options
  }

  async customSubmit(options) {
    const { formData, caller } = options

    if (formData.waFlowInitialAdd) {
      delete formData.waFlowInitialAdd
    }else if (formData.waFlowInitialBulkCopy) {
      delete formData.waFlowInitialBulkCopy
      await this.handleBulkCopyFlowSubmit(formData, caller)
      return
    } else if (formData.waFlowInitialCopy) {
      delete formData.waFlowInitialCopy

      const err = validateFlowFormBeforeSubmit(formData)
      if (err) {
        caller.setState({
          inProcess: false,
          success: false,
          message: err,
        })
        return
      }

      const singleScreenFlow = buildSingleScreenFlowFromForm(formData)
      const targetWaba = formData.wabaNumber
      const sourceWaba = this.flowCopySourceWaba
      const isDifferentWaba = sourceWaba && targetWaba !== sourceWaba
      try {
        const data = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['hooks'],
          queryParams: withVendorProjectIdQueryParam({
            orgId: this.orgId,
            bridgePath: 'whatsappflows',
          }, targetWaba, this.crmNumbers),
          jsonBody: {
            fromNumber: targetWaba,
            singleScreenFlow,
          },
        })
        const bridgeErr = hooksBridgeErrorMessage(data)
        if (bridgeErr) {
          caller.setState({
            inProcess: false,
            success: false,
            message: bridgeErr,
          })
          return
        }
        this.setState({ modalOpen: false }, () => this.fetchItems())
        caller.setState({
          inProcess: false,
          success: true,
          message: '',
        })
        if (isDifferentWaba) {
          this.showMessage(`Flow copied successfully. Switch to ${targetWaba} to view the flow.`, undefined, 'Success')
        } else {
          this.showMessage('Flow copied successfully', undefined, 'Success')
        }
      } catch (e) {
        caller.setState({
          inProcess: false,
          success: false,
          message: Helper.getErrorMsg(e),
        })
        this.flowCopySourceWaba = null
      }
      return
    } else {
      return caller.submit.call(caller, formData, true)
    }

    const err = validateFlowFormBeforeSubmit(formData)
    if (err) {
      caller.setState({
        inProcess: false,
        success: false,
        message: err,
      })
      return
    }

    const singleScreenFlow = buildSingleScreenFlowFromForm(formData)
    const wabaNumber = formData.wabaNumber
    try {
      const data = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'POST',
        paths: ['hooks'],
        queryParams: withVendorProjectIdQueryParam({
          orgId: this.orgId,
          bridgePath: 'whatsappflows',
        }, wabaNumber, this.crmNumbers),
        jsonBody: {
          fromNumber: wabaNumber,
          singleScreenFlow,
        },
      })
      const bridgeErr = hooksBridgeErrorMessage(data)
      if (bridgeErr) {
        caller.setState({
          inProcess: false,
          success: false,
          message: bridgeErr,
        })
        return
      }
      this.setState({ modalOpen: false }, () => this.fetchItems())
      caller.setState({
        inProcess: false,
        success: true,
        message: '',
      })
      this.showMessage('The flow was submitted successfully.', undefined, 'Success')
    } catch (e) {
      caller.setState({
        inProcess: false,
        success: false,
        message: Helper.getErrorMsg(e),
      })
    }
  }
  openBulkCopyFlowDialog = async (current) => {
    if (!current || !current.id || !current.wabaNumber) {
      this.showMessage('Unable to bulk copy this flow.', undefined, 'Error')
      return
    }

    try {
      const hookData = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'GET',
        paths: ['hooks', current.id],
        queryParams: withVendorProjectIdQueryParam({
          orgId: this.orgId,
          fromNumber: current.wabaNumber,
          bridgePath: 'whatsappflows',
        }, current.wabaNumber, this.crmNumbers),
      })

      if (!hookData || !hookData._flowJson) {
        this.showMessage('Unable to fetch flow details for bulk copying.', undefined, 'Error')
        return
      }

      const flowJson = hookData._flowJson
      const screens = flowJson.screens || []
      if (screens.length === 0) {
        this.showMessage('Flow has no screens. Cannot create a copy.', undefined, 'Error')
        return
      }

      const screen = screens[0]
      const screenTitle = screen.title || ''

      if (!screenTitle) {
        this.showMessage('Screen title is missing. Cannot create a copy.', undefined, 'Error')
        return
      }

      let formFields = []
      if (screen.layout && screen.layout.children && screen.layout.children.length > 0) {
        const layoutChild = screen.layout.children[0]
        if (layoutChild.children) {
          formFields = layoutChild.children
        }
      }

      const parsedFields = []
      let footerButtonLabel = ''

      for (let i = 0; i < formFields.length && parsedFields.length < MAX_FLOW_FIELDS; i += 1) {
        const field = formFields[i]
        const fieldType = field.type

        if (fieldType === 'Footer') {
          footerButtonLabel = field.label || ''
          continue
        }

        if (!FLOW_FIELD_TYPES_NOT_NONE.includes(fieldType)) {
          continue
        }

        const label = field.label || ''
        const required = field.required === true

        const entry = {
          type: fieldType,
          label,
          required,
        }

        if (FLOW_GROUP_TYPES.includes(fieldType) && field['data-source']) {
          const options = field['data-source']
            .map(opt => opt.title || opt.id)
            .join('|')
          entry.options = options
        }

        parsedFields.push(entry)
      }

      if (!footerButtonLabel) {
        this.showMessage(
          'Footer button label is missing. Cannot create a copy.',
          undefined,
          'Error'
        )
        return
      }

      const fromNumbers = this.state.waFromNumbers || []
      const fd = JSON.parse(JSON.stringify(buildBulkCopyFlowFormFieldDef(
        buildWaNumberSelectOptions(fromNumbers, this.state.waNumberDetails),
        current.wabaNumber
      )))

      this.flowBulkCopySourceWaba = current.wabaNumber
      this.flowBulkCopySourceName = current.name
      this.flowBulkCopyParsedFields = parsedFields
      this.flowBulkCopyScreenTitle = screenTitle
      this.flowBulkCopyFooterButtonLabel = footerButtonLabel

      Helper.deleteFormDefinitionValues(fd)

      this.formDefinition = fd
      this.setState({
        modalOpen: true,
        apiData: { waFlowInitialBulkCopy: true },
        apiMethod: 'POST',
        apiEndPoint: '',
      })
    } catch (e) {
      this.showMessage('Unable to load flow details for bulk copying.', undefined, 'Error')
    }
  }

  handleBulkCopyFlowSubmit = async (formData, caller) => {
    const selectedWabas = formData.selectedWabas || []
    const selectedWabaKeys = Array.isArray(selectedWabas)
      ? selectedWabas.map(waba => typeof waba === 'string' ? waba : waba.key)
      : []

    if (selectedWabaKeys.length === 0) {
      caller.setState({
        inProcess: false,
        success: false,
        message: 'Select at least one WABA number',
      })
      return
    }

    if (selectedWabaKeys.length > 10) {
      caller.setState({
        inProcess: false,
        success: false,
        message: 'You can copy to a maximum of 10 WABA numbers at a time',
      })
      return
    }

    const copyPromises = selectedWabaKeys.map(async (targetWabaNumber) => {
      try {
        if (!this.flowBulkCopyParsedFields || !this.flowBulkCopyScreenTitle || !this.flowBulkCopyFooterButtonLabel) {
          return { success: false, wabaNumber: targetWabaNumber, error: 'Missing flow data' }
        }

        const parsedFields = this.flowBulkCopyParsedFields || []
        const screenTitle = this.flowBulkCopyScreenTitle || ''
        const footerButtonLabel = this.flowBulkCopyFooterButtonLabel || ''

        const singleScreenFlow = {
          name:`${this.flowBulkCopySourceName}`,
          screenTitle,
          footerButtonLabel,
          fields: parsedFields,
        }

        const data = await ApiHelper.callAwait({
          ms: this.ms,
          method: 'POST',
          paths: ['hooks'],
          queryParams: withVendorProjectIdQueryParam({
            orgId: this.orgId,
            bridgePath: 'whatsappflows',
          }, targetWabaNumber, this.crmNumbers),
          jsonBody: {
            fromNumber: targetWabaNumber,
            singleScreenFlow,
          },
        })

        const bridgeErr = hooksBridgeErrorMessage(data)
        if (bridgeErr) {
          return { success: false, wabaNumber: targetWabaNumber, error: bridgeErr }
        }

        return { success: true, wabaNumber: targetWabaNumber }
      } catch (err) {
        console.error(`Error copying flow to ${targetWabaNumber}:`, err)
        return { success: false, wabaNumber: targetWabaNumber, error: err.message }
      }
    })

    const results = await Promise.all(copyPromises)

    const successCount = results.filter(r => r.success).length
    const failedNumbers = results.filter(r => !r.success).map(r => buildWaNumberLabel(r.wabaNumber, this.waNumberDetails))

    this.setState({ modalOpen: false }, () => this.fetchItems())

    if (failedNumbers.length === 0) {
      caller.setState({
        inProcess: false,
        success: true,
        message: '',
      })
      this.showMessage(
        `Flow copied successfully to ${successCount} WABA number(s)`,
        undefined,
        'Success'
      )
    } else if (successCount > 0) {
      caller.setState({
        inProcess: false,
        success: true,
        message: '',
      })
      this.showMessage(
        `Flow copied to ${successCount} WABA(s). Failed for: ${failedNumbers.join(', ')}`,
        undefined,
        'Warning'
      )
    } else {
      caller.setState({
        inProcess: false,
        success: false,
        message: 'Failed to copy flow to all WABA numbers',
      })

      this.showMessage(
        'Failed to copy flow to all WABA numbers',
        undefined,
        'Error'
      )
    }
  }
  onWabaFilterChange = async (e, val) => {
    if (!val || !val.key) return
    if (this.state.wabaFilterSelected && this.state.wabaFilterSelected.key === val.key) return
    const cachedRows = this.state.flowsByWaba && this.state.flowsByWaba[val.key]
    if (cachedRows) {
      this.applyRowsForSelectedWaba(cachedRows, val)
      return
    }
    try {
      this.setState({ wabaFlowsLoading: true })
      const rows = await this.buildRowsForWaba(val.key)
      this.applyRowsForSelectedWaba(rows, val, {
        flowsByWaba: {
          ...(this.state.flowsByWaba || {}),
          [val.key]: rows,
        },
        wabaFlowsLoading: false,
      })
    } catch (err) {
      this.setState({ wabaFlowsLoading: false })
      this.showMessage(Helper.getErrorMsg(err), undefined, 'Error')
    }
  }

  onSearch = (searchText = '') => {
    const items = filterFlowRows(
      this.state.masterFlowList,
      this.state.wabaFilterSelected,
      searchText
    )
    this.setState({ searchText, items })
  }

  clearSearch = () => {
    const items = filterFlowRows(
      this.state.masterFlowList,
      this.state.wabaFilterSelected,
      ''
    )
    this.setState({
      searchText: '',
      items,
    })
  }

  fetchFlowPreviewJson = async ({ downloadUrl }) => {
    const data = await ApiHelper.callAwait({
      ms: this.ms,
      method: 'GET',
      paths: ['miscs', 'fetch-proxy'],
      queryParams: {
        url: downloadUrl,
      },
    })
    if (!data || !data.text) {
      throw new Error('Flow preview response text is empty.')
    }
    return JSON.parse(data.text)
  }

  openPreviewDialog = async (current) => {
    const id = current && current.id ? String(current.id) : ''
    const fromNumber = current && current.wabaNumber ? String(current.wabaNumber) : ''
    if (!id || !fromNumber) {
      this.showMessage('Flow preview is unavailable for this record.', undefined, 'Error')
      return
    }
    try {
      this.setState({ flowPreviewLoadingId: id })
      const hookData = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'GET',
        paths: ['hooks', id],
        queryParams: withVendorProjectIdQueryParam({
          orgId: this.orgId,
          fromNumber,
          bridgePath: 'whatsappflows',
        }, fromNumber, this.crmNumbers),
      })
      this.setState({
        flowPreviewLoadingId: '',
        isFlowPreviewDialogOpen: true,
        flowPreviewTitle: current.name || 'Flow Preview',
        flowPreviewPayload: (hookData && hookData._flowJson) ? hookData._flowJson : null,
      })
    } catch (err) {
      this.setState({ flowPreviewLoadingId: '' })
      this.showMessage('Unable to load flow preview.', undefined, 'Error')
    }
  }

  renderFlowPreviewDialog() {
    return (
      <PageDialog
        PageComponent={WhatsappFlowPreview}
        PageComponentProps={{
          flowScreens: (this.state.flowPreviewPayload && this.state.flowPreviewPayload.screens) || [],
        }}
        title={this.state.flowPreviewTitle || 'Flow Preview'}
        fullWidthDialog={false}
        isOpen={this.state.isFlowPreviewDialogOpen}
        onClosed={() => {
          this.setState({
            isFlowPreviewDialogOpen: false,
            flowPreviewTitle: '',
            flowPreviewPayload: null,
          })
        }}
      />
    )
  }

  renderBelowTable() {
    if (!this.state.isFlowPreviewDialogOpen) return null
    return this.renderFlowPreviewDialog()
  }

  renderAboveTable() {
    const wabaOptions = this.state.wabaOptions && this.state.wabaOptions.length > 0
      ? this.state.wabaOptions
      : []
    const wabaValue = this.state.wabaFilterSelected || wabaOptions[0] || null
    const multiWaba = wabaOptions.length > 1
    const showToolbarRefreshSpinner = (this.state.fetchState === ApiHelper.State.LOADING
      && this.state.loadingMode === ApiHelper.LOADING_MODE.INSTANT_LOAD)
      || this.state.wabaFlowsLoading

    return (
      <div className={css(Styles.toolbar)}>
        <div className={css(Styles.filterContainer)}>
          <div className={css(Styles.formControlContainer)}>
            <p className={css(Styles.label)}>WABA number</p>
            <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
              <Autocomplete
                value={wabaValue}
                options={wabaOptions}
                onChange={(e, val) => this.onWabaFilterChange(e, val)}
                getOptionLabel={option => option.label || option.key || ''}
                getOptionSelected={(option, v) => option.key === v.key}
                disableClearable
                forcePopupIcon={multiWaba}
                popupIcon={multiWaba ? undefined : null}
                open={multiWaba ? undefined : false}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant='standard'
                    className={css(Styles.formControlFilters)}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      readOnly: true,
                      classes: {
                        root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                        input: css(Styles.formInputStyle, Styles.textInput),
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className={css(Styles.refreshActionContainer)}>
            <p className={css(Styles.label)}>&nbsp;</p>
            <div className={css(Styles.refreshActionSlot)}>
              {!showToolbarRefreshSpinner && (
                <Tooltip title='Refresh' placement='top-start'>
                  <IconButton
                    className={css(Styles.refreshIconButton)}
                    onClick={() => this.fetchItems()}
                    aria-label='Refresh'
                  >
                    <i
                      className='material-icons-outlined'
                      style={{ fontSize: 24, color: AppTheme.primaryColor }}
                    >
                      refresh
                    </i>
                  </IconButton>
                </Tooltip>
              )}
              {showToolbarRefreshSpinner && UiHelper.itemLoadingViewButton(24)}
            </div>
          </div>
        </div>
        <div className={css(Styles.searchBarContainer)} onSubmit={e => e.preventDefault()}>
          <SearchBar
            hint='Search by flow name'
            onSearch={this.onSearch}
            onClearSearch={this.clearSearch}
            searchText={this.state.searchText}
            searchButtonLabel='Filter'
            clearSearchLabel='Clear'
            autoRefresh
            debounceDuration={1}
            autoFocus={false}
            hideFiltersContainer
          />
        </div>
      </div>
    )
  }

  onActions(current) {
    const isLoadingPreview = this.state.flowPreviewLoadingId === current.id
    return [
      <Tooltip title='Preview' placement='top-start' key={`${current.id}-preview`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <span className={css(Styles.previewActionSlot)}>
            {!isLoadingPreview && (
              <i
                className='material-icons-outlined'
                onClick={(e) => {
                  e.stopPropagation()
                  this.openPreviewDialog(current)
                }}
              >
                desktop_windows
              </i>
            )}
            {isLoadingPreview && <CircularProgress size={18} thickness={5} />}
          </span>
        </span>
      </Tooltip>,
    ]
  }

  onMenuItems = (current) =>{
    const actions = []
    const menuItems = [
      {
        current: current,
        key: current.id + 'create-copy',
        action: () => {
          this.openCopyTemplateDialog(current)
        },
        title: Helper.getString('createcopy'),
        icon: 'copy',
      },
      {
        current: current,
        key: current.id,
        action: () => {
          this.openBulkCopyFlowDialog(current)
        },
        title: 'Bulk Copy',
        icon: 'copy_all',
      },
    ]

    menuItems.map(item => {
      actions.push(UiHelper.actionButton(item))
    })
    return actions
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    marginBottom: 16,
  },
  filterContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
    overflowX: 'hidden',
    overflowY: 'visible',
    minWidth: 0,
    '@media (min-width: 1024px)': {
      flexWrap: 'nowrap',
    },
  },
  refreshActionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-end',
    gap: 5,
    flex: '0 0 auto',
    minWidth: 48,
  },
  refreshActionSlot: {
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  refreshIconButton: {
    padding: 8,
  },
  formControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
    flex: '1 1 220px',
    minWidth: 0,
    '@media (min-width: 1024px)': {
      flex: '1 1 0%',
      minWidth: 0,
    },
  },
  label: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    display: 'inline-block',
  },
  formControlMainFilters: {
    width: '100%',
    '@media (max-width: 767px)': {
      width: '100%',
    },
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  autoCompleteInputRoot: {
    paddingRight: 48,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  textInput: {
    padding: '4px 0px',
  },
  searchBarContainer: {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
  line1: {
    fontSize: 16,
  },
  previewActionSlot: {
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '16px',
    padding: '4px 10px',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  statusBadgeDefault: {
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
  },
  statusBadgeSuccess: {
    backgroundColor: '#3BDE86',
    color: '#222222',
  },
  statusBadgePending: {
    backgroundColor: '#FFD7BB',
    color: '#222222',
  },
  statusBadgeError: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
  },
})
